import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Webcam from 'react-webcam';
import { Box, Typography, IconButton, Fade, useTheme } from '@mui/material';
import { PlayArrow, Pause, Stop } from '@mui/icons-material';
import toast from 'react-hot-toast';
import { FilesetResolver, PoseLandmarker } from '@mediapipe/tasks-vision';
import inhaleSoundSrc from '../../assets/effects/inhale.mp3';
import exhaleSoundSrc from '../../assets/effects/exhale.mp3';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { MarkComplete } from '../../services/pranayamma/markComplete';
import { createPranayamaReport } from 'services/pranayamaReport/createPranayamaReport';

const PranayamaSession = () => {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const {
    courseId,
    selectedId: pranayamaId,
    name,
    breathing_sequence,
    loops: totalLoops = 1,
    instructions = '',
  } = location.state || {};

  const isContinuous = location.state?.isContinuousFlow === true;
  const currentIndex = location.state?.currentIndex ?? 0;
  const totalPractices = location.state?.allPranayamas?.length ?? 1;

  const [startTime, setStartTime] = useState(null);
  const [interruptCount, setInterruptCount] = useState(0);
  const [phaseIndex, setPhaseIndex] = useState(-1);
  const [currentLoop, setCurrentLoop] = useState(1);
  const [secondsLeft, setSecondsLeft] = useState(6); // Changed to 6
  const [isRunning, setIsRunning] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [showMiniCam, setShowMiniCam] = useState(true);
  const [isPersonDetected, setIsPersonDetected] = useState(false);
  const [poseLandmarker, setPoseLandmarker] = useState(null);
  const [isInitialCountdown, setIsInitialCountdown] = useState(false);
  const [detectionWaiting, setDetectionWaiting] = useState(true);
  const [wasPausedByDetection, setWasPausedByDetection] = useState(false);
  const [cameraReady, setCameraReady] = useState(false);
  const [hasStartedOnce, setHasStartedOnce] = useState(false);

  const webcamRef = useRef(null);
  const animationFrameRef = useRef(null);
  const countdownTimerRef = useRef(null);
  const speechFailSafeRef = useRef(null);
  const inhaleAudio = useRef(null);
  const exhaleAudio = useRef(null);

  // Audio setup with safe cleanup
  useEffect(() => {
    inhaleAudio.current = new Audio(inhaleSoundSrc);
    exhaleAudio.current = new Audio(exhaleSoundSrc);
    inhaleAudio.current.playbackRate = 0.45;
    exhaleAudio.current.playbackRate = 0.45;

    return () => {
      pauseAudio(); // safe call
      inhaleAudio.current = null;
      exhaleAudio.current = null;
    };
  }, []);

  const markCompleteMutation = useMutation({
    mutationFn: MarkComplete,
    onSuccess: () => {
      toast.success('Practice marked as completed!');
      queryClient.invalidateQueries(['pranayama']);
    },
    onError: (error) => toast.error(error.message || 'Failed to save completion'),
  });

  const createReportMutation = useMutation({
    mutationFn: createPranayamaReport,
    onSuccess: (data) => {
      console.log('Pranayama report created:', data);
      toast.success('Pranayama report saved!');
    },
    onError: (error) => {
      console.error('Failed to save pranayama report:', error);
      toast.error('Failed to save report');
    },
  });

  const stopAllSpeech = () => {
    window.speechSynthesis?.cancel();
    if (speechFailSafeRef.current) {
      clearTimeout(speechFailSafeRef.current);
      speechFailSafeRef.current = null;
    }
  };

  const pauseAudio = () => {
    // Fully null-safe
    if (inhaleAudio.current) {
      try {
        inhaleAudio.current.pause();
        inhaleAudio.current.currentTime = 0;
      } catch {}
    }
    if (exhaleAudio.current) {
      try {
        exhaleAudio.current.pause();
        exhaleAudio.current.currentTime = 0;
      } catch {}
    }
  };

  const speak = (text, options = {}) => {
    if (!('speechSynthesis' in window) || !text?.trim()) return;
    if (options.cancelPrevious !== false) {
      stopAllSpeech();
    }

    const utterance = new SpeechSynthesisUtterance(text);
    utterance.rate = options.rate ?? 0.85;
    utterance.pitch = 1.0;
    utterance.volume = 0.9;

    if (options.onend) {
      utterance.onend = () => {
        if (speechFailSafeRef.current) clearTimeout(speechFailSafeRef.current);
        options.onend();
      };
      speechFailSafeRef.current = setTimeout(options.onend, 6000);
    }

    window.speechSynthesis.speak(utterance);
  };

  // Global cleanup
  useEffect(() => {
    return () => {
      stopAllSpeech();
      pauseAudio();
      if (countdownTimerRef.current) {
        clearTimeout(countdownTimerRef.current);
        countdownTimerRef.current = null;
      }
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
      if (poseLandmarker?.close) poseLandmarker.close();
    };
  }, [poseLandmarker]);

  const sequence = useMemo(() => {
    try {
      return breathing_sequence ? JSON.parse(breathing_sequence) : [];
    } catch {
      return [];
    }
  }, [breathing_sequence]);

  // Initial Welcome
  useEffect(() => {
    setCameraReady(false);
    const welcomeText = `Welcome to ${name || 'your session'}.`;
    speak(welcomeText, {
      rate: 0.8,
      onend: () => {
        const instructionText = instructions.trim() ? instructions : 'Breathe naturally and follow the rhythm.';
        speak(instructionText, {
          rate: 0.8,
          onend: () => {
            setCameraReady(true);
            speak('Please position yourself in frame to start.', { rate: 0.8 });
          },
        });
      },
    });

    return () => stopAllSpeech();
  }, [pranayamaId, name, instructions]);

  // Load MediaPipe
  useEffect(() => {
    if (!cameraReady) return;
    const init = async () => {
      try {
        const vision = await FilesetResolver.forVisionTasks(
          'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.0/wasm'
        );
        const landmarker = await PoseLandmarker.createFromOptions(vision, {
          baseOptions: {
            modelAssetPath:
              'https://storage.googleapis.com/mediapipe-models/pose_landmarker/pose_landmarker_lite/float16/1/pose_landmarker_lite.task',
            delegate: 'GPU',
          },
          runningMode: 'VIDEO',
          numPoses: 1,
        });
        setPoseLandmarker(landmarker);
      } catch (err) {
        console.error(err);
        setDetectionWaiting(false);
        startSession(false);
      }
    };
    init();
  }, [cameraReady]);

  // Detection loop
  useEffect(() => {
    if (!poseLandmarker || !webcamRef.current?.video || !cameraReady) return;

    let isActive = true;

    const detect = async () => {
      if (!isActive) return;
      const video = webcamRef.current?.video;
      if (video?.readyState >= 2) {
        try {
          const results = await poseLandmarker.detectForVideo(video, performance.now());
          setIsPersonDetected(!!results.landmarks?.[0]?.length);
        } catch (err) {
          console.warn(err);
        }
      }
      if (isActive) animationFrameRef.current = requestAnimationFrame(detect);
    };

    detect();

    return () => {
      isActive = false;
      if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
    };
  }, [poseLandmarker, cameraReady]);

  const startSession = (isRestart = true) => {
    if (countdownTimerRef.current) clearTimeout(countdownTimerRef.current);
    stopAllSpeech();
    pauseAudio();

    setPhaseIndex(-1);
    setCurrentLoop(1);
    setIsRunning(false);
    setIsInitialCountdown(true);
    setSecondsLeft(6); // Changed to 6

    if (!startTime) setStartTime(Date.now());

    const readyText = isRestart ? "Position detected. Let's restart. Get ready." : 'Position detected. Get ready.';

    setTimeout(() => {
      speak(readyText, {
        rate: 0.85,
        onend: () => {
          setHasStartedOnce(true);
          startSlowCountdown(6, () => { // Changed to 6
            setPhaseIndex(0);
            setIsRunning(true);
            setIsInitialCountdown(false);
          });
        },
      });
    }, 150);
  };

  const startSlowCountdown = (initialSeconds, onComplete) => {
    if (countdownTimerRef.current) clearTimeout(countdownTimerRef.current);

    const runStep = (current) => {
      if (current < 0) {
        onComplete?.();
        return;
      }

      setSecondsLeft(current);

      if (current > 0) {
        speak(current.toString(), {
          rate: 1.2,
          onend: () => {
            countdownTimerRef.current = setTimeout(() => runStep(current - 1), 550);
          },
        });
      } else {
        countdownTimerRef.current = setTimeout(() => onComplete?.(), 400);
      }
    };

    runStep(initialSeconds);
  };

  // Breathing phase
  useEffect(() => {
    if (phaseIndex < 0 || !isRunning || isPaused || !sequence.length) return;

    if (window.speechSynthesis?.speaking || window.speechSynthesis?.pending) {
      return;
    }

    const currentPhase = sequence[phaseIndex];
    const typeLower = currentPhase.type?.toLowerCase() || '';
    const instruction = typeLower.includes('inhale')
      ? 'Inhale'
      : typeLower.includes('exhale')
      ? 'Exhale'
      : 'Hold';

    speak(instruction, {
      rate: 0.85,
      onend: () => {
        if (isPaused) return;

        if (typeLower.includes('inhale')) inhaleAudio.current?.play().catch(() => {});
        if (typeLower.includes('exhale')) exhaleAudio.current?.play().catch(() => {});

        startSlowCountdown(currentPhase.seconds || 4, () => {
          if (phaseIndex + 1 < sequence.length) {
            setPhaseIndex(phaseIndex + 1);
          } else if (currentLoop < totalLoops) {
            setCurrentLoop(currentLoop + 1);
            setPhaseIndex(0);
          } else {
            handleComplete();
          }
        });
      },
    });

    return () => {
      stopAllSpeech();
      pauseAudio();
      if (countdownTimerRef.current) clearTimeout(countdownTimerRef.current);
    };
  }, [phaseIndex, isRunning, isPaused, sequence]);

  const handleComplete = () => {
    const sessionDuration = startTime ? Math.floor((Date.now() - startTime) / 1000) : 0;

    markCompleteMutation.mutate({
      pranayama_id: pranayamaId,
      course_id: courseId,
      student_id: Number(localStorage.getItem('userid')),
    });

    const reportData = {
      studentId: Number(localStorage.getItem('userid')),
      pranayamaId: pranayamaId,
      courseId: courseId,
      totalDuration: sessionDuration,
      totalLoopsCompleted: currentLoop - 1,
      interruptions: interruptCount,
      pranayamaImage: null,
      feedBack: null,
    };

    createReportMutation.mutate(reportData);

    speak('Session complete. Well done.', {
      rate: 0.8,
      onend: () => {
        setTimeout(() => {
          if (isContinuous && currentIndex < (location.state?.allPranayamas?.length ?? 0) - 1) {
            const nextIndex = currentIndex + 1;
            const nextPran = location.state.allPranayamas[nextIndex];

            navigate('/pranayama-tracking', {
              state: {
                courseId,
                selectedId: nextPran.id,
                name: nextPran.name,
                breathing_sequence: nextPran.breathing_sequence,
                duration: nextPran.duration,
                loops: nextPran.loops,
                instructions: nextPran.instructions,
                isContinuousFlow: true,
                currentIndex: nextIndex,
                allPranayamas: location.state.allPranayamas,
                phaseIndex: -1,
                currentLoop: 1,
                sequenceType: 'continuous',
              },
              replace: true,
            });

            speak(`Starting next: ${nextPran.name}`, { rate: 0.85 });
          } else {
            const targetUrl = isContinuous ? `/asanas/${courseId}` : `/asanas/${courseId}/standalone`;

            navigate(targetUrl, {
              state: {
                activeTab: 1,
                justCompletedId: pranayamaId,
                sequenceType: isContinuous ? 'continuous' : 'standalone',
              },
              replace: true,
            });
          }
        }, 1800);
      },
    });
  };

  useEffect(() => {
  if (!cameraReady || !poseLandmarker) return;

  // 1. If person leaves the frame
  if (!isPersonDetected) {
    // Only trigger pause if we were actually running and not already waiting
    if (!isPaused && !detectionWaiting) {
      setIsPaused(true);
      setWasPausedByDetection(true);
      setInterruptCount((c) => c + 1);

      if (countdownTimerRef.current) clearTimeout(countdownTimerRef.current);
      pauseAudio();
      stopAllSpeech();
      speak('Please return to the frame.', { rate: 0.85 });
    }
  } 
  // 2. If person is detected AND we were previously paused/waiting
  else if (isPersonDetected && (wasPausedByDetection || detectionWaiting)) {
    // CRITICAL: Double check the webcam reference before restarting
    const video = webcamRef.current?.video;
    if (video && video.readyState >= 2) {
      setWasPausedByDetection(false);
      setDetectionWaiting(false);
      setIsPaused(false);
      
      // Only start if this is a legitimate return to frame
      startSession(wasPausedByDetection || hasStartedOnce);
    }
  }
}, [isPersonDetected, cameraReady, poseLandmarker]);
  const getScale = () => {
    if (isInitialCountdown || phaseIndex < 0) return 1;
    const currentPhase = sequence[phaseIndex];
    const total = currentPhase?.seconds || 4;
    const progress = 1 - secondsLeft / total;
    const type = currentPhase?.type?.toLowerCase() || '';

    if (type.includes('inhale')) return 1 + progress * 1.6;
    if (type.includes('exhale')) return 2.6 - progress * 1.6;
    return 2.6;
  };

  if (!pranayamaId || sequence.length === 0) return null;

  return (
    <Box
      sx={{
        bgcolor: '#0f172a',
        color: 'white',
        height: '75dvh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Title */}
      <Box sx={{ textAlign: 'center', position: 'absolute', top: '5%', zIndex: 10, px: 3 }}>
        <Typography variant="h4" fontWeight="bold">
          {isContinuous ? `Practice ${currentIndex + 1} of ${totalPractices}` : name}
        </Typography>
      </Box>

      {/* Breathing Orb */}
      <Box
        sx={{
          width: { xs: '220px', sm: '320px', md: '400px' },
          height: { xs: '220px', sm: '320px', md: '400px' },
          borderRadius: '50%',
          background: 'radial-gradient(circle at 40% 40%, rgba(96,165,250,0.25), transparent 70%)',
          transform: `scale(${getScale()})`,
          transition: 'transform 0.8s linear',
          boxShadow: '0 0 80px rgba(96,165,250,0.3)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 5,
        }}
      >
        <Typography variant="h1" sx={{ fontSize: { xs: '4rem', sm: '5rem' } }}>
          {secondsLeft}
        </Typography>
      </Box>

      {/* Status text */}
      <Typography
        variant="h5"
        sx={{
          position: 'absolute',
          bottom: '20%',
          fontWeight: 500,
          zIndex: 10,
          textAlign: 'center',
          px: 2,
        }}
      >
        {isInitialCountdown
          ? 'Preparing...'
          : detectionWaiting
          ? 'Waiting for you...'
          : isPaused
          ? 'Paused'
          : `${sequence[phaseIndex]?.type?.toUpperCase() || '—'} • Loop ${currentLoop}/${totalLoops}`}
      </Typography>

      {/* Mini cam */}
      {cameraReady && showMiniCam && (
        <Box
          sx={{
            position: 'absolute',
            top: 16,
            right: 16,
            width: 140,
            height: 180,
            borderRadius: '12px',
            overflow: 'hidden',
            border: `3px solid ${isPersonDetected ? '#4caf50' : '#ff9800'}`,
            zIndex: 20,
          }}
        >
          <Webcam
            ref={webcamRef}
            audio={false}
            videoConstraints={{ facingMode: 'user' }}
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        </Box>
      )}

      <Fade in={cameraReady && !isPersonDetected && !detectionWaiting}>
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            bgcolor: 'rgba(0,0,0,0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 25,
            backdropFilter: 'blur(4px)',
          }}
        >
          <Typography variant="h6">Please return to the frame</Typography>
        </Box>
      </Fade>

      <Box sx={{ position: 'absolute', bottom: '5%', display: 'flex', gap: 2, zIndex: 30 }}>
        <IconButton
          onClick={() => setIsPaused(!isPaused)}
          sx={{ bgcolor: '#2e7d32', color: 'white', '&:hover': { bgcolor: '#1b5e20' } }}
        >
          {isPaused ? <PlayArrow fontSize="large" /> : <Pause fontSize="large" />}
        </IconButton>

        <IconButton
          onClick={() => {
            stopAllSpeech();
            pauseAudio();
            const targetUrl = isContinuous ? `/asanas/${courseId}` : `/asanas/${courseId}/standalone`;
            navigate(targetUrl, {
              state: {
                activeTab: 1,
                justCompletedId: pranayamaId,
                sequenceType: isContinuous ? 'continuous' : 'standalone',
              },
              replace: true,
            });
          }}
          sx={{
            border: '2px solid #d32f2f',
            color: '#d32f2f',
            '&:hover': { backgroundColor: 'rgba(211, 47, 47, 0.08)' },
          }}
          title="Stop Session"
        >
          <Stop fontSize="medium" />
        </IconButton>
      </Box>
    </Box>
  );
};

export default PranayamaSession;